import {
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
} from "@t3tools/client-runtime/state/runtime";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

import { environmentCatalog } from "../../connection/catalog";
import { useCloudEnvironmentControl } from "../../state/cloudEnvironmentControl";
import { useEnvironments } from "../../state/environments";
import { relayEnvironmentDiscovery } from "../../state/relay";
import { useAtomCommand } from "../../state/use-atom-command";
import { selectCloudEnvironments } from "./CloudSettingsPanel.logic";
import { FabricaCloudSetupPanel } from "./CloudSetupPanel";
import { SettingsPageContainer } from "./settingsLayout";

/**
 * Fabrica Cloud owns its own settings section. Unlike the Providers page, it
 * has no device picker: the page always means the Cloud environment, and the
 * panel guides first-run setup while none exists.
 */
export function CloudSettingsPanel() {
  const navigate = useNavigate();
  const { environments, isReady } = useEnvironments();
  const cloudEnvironments = useMemo(() => selectCloudEnvironments(environments), [environments]);
  const cloudEnvironment = cloudEnvironments[0] ?? null;

  const retryEnvironment = useAtomCommand(environmentCatalog.retryNow, { reportFailure: false });
  const refreshRelayEnvironments = useAtomCommand(relayEnvironmentDiscovery.refresh, {
    reportFailure: false,
  });
  const cloudEnvironmentControl = useCloudEnvironmentControl();

  const openConnections = useCallback(() => {
    void navigate({ to: "/settings/connections", hashScrollIntoView: false });
  }, [navigate]);
  const openProviders = useCallback(() => {
    void navigate({ to: "/settings/providers", hashScrollIntoView: false });
  }, [navigate]);
  const refreshCloudConnections = useCallback(async () => {
    await refreshRelayEnvironments();
  }, [refreshRelayEnvironments]);

  const retryCloudEnvironment = useCallback(async () => {
    if (cloudEnvironment === null) return;
    const cloudPhase = cloudEnvironment.connection.phase;
    if (cloudPhase === "available" || cloudPhase === "offline") {
      if (!cloudEnvironmentControl.available) {
        throw new Error("The local Fabrica environment is not ready yet.");
      }
      // A registered Cloud target is not merely a disconnected socket: Resume
      // must ask Fabrica's managed control service to start the runtime first. Relay
      // discovery then picks up the runtime's fresh endpoint/credential.
      await cloudEnvironmentControl.provisionOrResume();
      await refreshRelayEnvironments();
      return;
    }
    if (cloudPhase !== "error") return;
    const result = await retryEnvironment(cloudEnvironment.environmentId);
    if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
      const error = squashAtomCommandFailure(result);
      throw error instanceof Error ? error : new Error("Could not reconnect Cloud.");
    }
  }, [cloudEnvironment, cloudEnvironmentControl, refreshRelayEnvironments, retryEnvironment]);

  return (
    <SettingsPageContainer>
      <FabricaCloudSetupPanel
        key={cloudEnvironment?.environmentId ?? "unset"}
        selectedEnvironment={cloudEnvironment}
        cloudEnvironmentCount={cloudEnvironments.length}
        catalogReady={isReady}
        onOpenConnections={openConnections}
        onOpenProviders={openProviders}
        onRetryEnvironment={retryCloudEnvironment}
        {...(cloudEnvironmentControl.available
          ? {
              onProvisionCloud: cloudEnvironmentControl.provisionOrResume,
              onCompleteCloudAuthorization: cloudEnvironmentControl.completeConnectAuthorization,
              onStopCloud: cloudEnvironmentControl.stopCloud,
              onRefreshCloudConnections: refreshCloudConnections,
            }
          : {})}
      />
    </SettingsPageContainer>
  );
}
